import styled from 'styled-components';

import { Carousel, CarouselProps } from '@xing-com/carousel';
import { Ad, AdFields, SiteSections } from '@xing-com/crate-ads-native-ads';
import type { SimilarJobData } from '@xing-com/crate-jobs-helpers';
import { GridContainer, GridItem } from '@xing-com/grid';
import { mediaWideNavless } from '@xing-com/layout-tokens';
import { spaceL, spaceS, spaceXL, spaceXXL } from '@xing-com/tokens';
import { useMediaListener } from '@xing-com/util';

import { ModuleTitle } from '../module-title';
import { SimilarJob } from '../similar-job';

import { SimilarJobsLoading } from './similar-jobs-loading';

type SimilarJobsModuleProps = {
  isLoading: boolean;
  showCarousel?: boolean;
  similarJobs: SimilarJobData[];
  ads?: AdFields[];
  isLoadingAds?: boolean;
};

const StyledGridContainer = styled(GridContainer)`
  column-gap: ${spaceL};
  row-gap: ${spaceXXL};

  @media ${mediaWideNavless} {
    row-gap: ${spaceXL};
  }
`;

export const getCarouselConfig = (
  itemWidthMobile = '80%',
  itemWidthConfined = '45%',
  itemSpacing = spaceS
): Partial<CarouselProps> => ({
  // mobile
  itemWidth: itemWidthMobile,
  itemSpacing: itemSpacing,

  // tablet
  itemWidthConfined: itemWidthConfined,
  itemSpacingConfined: itemSpacing,

  // desktop only for the initial load of the page
  itemWidthWide: `calc((100% - ${spaceL} * 2) / 3)`,
  itemSpacingWide: spaceL,
});

export const SimilarJobsModule: React.FC<SimilarJobsModuleProps> = ({
  isLoading = false,
  similarJobs,
  showCarousel = false,
  ads = [],
  isLoadingAds = false,
}: SimilarJobsModuleProps) => {
  const breakpoint = useMediaListener();

  if (isLoading || isLoadingAds) {
    return (
      <div>
        <ModuleTitle titleKey="JOBS_JOB_DETAILS_SIMILAR_JOBS_HEADLINE" />
        <StyledGridContainer variant="FullSize">
          <SimilarJobsLoading count={3} isCarousel={showCarousel} />
        </StyledGridContainer>
      </div>
    );
  }
  if (similarJobs.length === 0) return null;
  return (
    <div>
      <ModuleTitle titleKey="JOBS_JOB_DETAILS_SIMILAR_JOBS_HEADLINE" />
      <StyledGridContainer variant="FullSize">
        {breakpoint !== 'desktop' && showCarousel ? (
          <GridItem span={12}>
            <Carousel {...getCarouselConfig()} tabIndex={-1}>
              {similarJobs.map((similarJob) => (
                <SimilarJob
                  ijtParam="jb_20"
                  isCarousel
                  key={similarJob.job.id}
                  similarJob={similarJob}
                />
              ))}
              {ads?.length > 0 ? (
                <Ad sectionId={SiteSections.JobsDetails} {...ads?.[0]} />
              ) : null}
            </Carousel>
          </GridItem>
        ) : (
          <>
            {similarJobs.map((similarJob, i) => (
              // i is needed to ensure unique keys in the list because if not when changing
              // the size of the screen we duplicate items in version A
              // (because top jobs move to the bottom)
              <GridItem
                key={`${similarJob.job.id} + ${i}`}
                span={12}
                spanLarge={4}
              >
                <SimilarJob similarJob={similarJob} ijtParam="jb_20" />
              </GridItem>
            ))}
            {ads?.length > 0 ? (
              <GridItem span={12} spanLarge={4}>
                <Ad sectionId={SiteSections.JobsDetails} {...ads?.[0]} />
              </GridItem>
            ) : null}
          </>
        )}
      </StyledGridContainer>
    </div>
  );
};
